import { useNavigate, useSearchParams } from "react-router-dom";
import { SlackInstallation } from "../types/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWarning,
  faArrowRightToBracket,
} from "@fortawesome/free-solid-svg-icons";

interface WorkspaceSwitcherProps {
  installations: SlackInstallation[];
  currentTeamId: string | null;
}

const WorkspaceSwitcher = ({
  installations,
  currentTeamId,
}: WorkspaceSwitcherProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleWorkspaceChange = (teamId: string, isActive: boolean) => {
    if (teamId === currentTeamId) return;

    // If the installation is not active, redirect to reconnect flow
    if (!isActive) {
      window.location.href = `https://core.trybraid.io/slack/install?team=${teamId}`;
      return;
    }

    // Otherwise proceed normally
    const redirectUrl = searchParams.get("redirect_url");
    if (redirectUrl) {
      const decodedUrl = decodeURIComponent(redirectUrl);
      const redirectSearchParams = new URLSearchParams(
        decodedUrl.split("?")[1] || "",
      );
      redirectSearchParams.set("team", teamId);

      const baseUrl = decodedUrl.split("?")[0];
      navigate(`${baseUrl}?${redirectSearchParams.toString()}`, {
        replace: true,
      });
    } else {
      navigate(`/profile?team=${teamId}`, { replace: true });
    }
  };

  // Handle reconnection with support for both mouse and keyboard events
  const handleReconnect = (teamId: string, event: React.SyntheticEvent) => {
    // Prevent the parent button click from firing
    event.stopPropagation();

    // Redirect to the Slack installation page
    window.location.href = `https://core.trybraid.io/slack/install?team=${teamId}`;
  };

  return (
    <div
      className="w-full grid gap-3"
      role="navigation"
      aria-label="Workspace switcher"
    >
      {installations.map((installation) => (
        <button
          key={installation.team_id}
          onClick={() =>
            handleWorkspaceChange(installation.team_id, installation.is_active)
          }
          className={`
            group relative w-full px-6 py-4 text-center
            rounded-xl transition-all duration-200 
            ${!installation.is_active ? "opacity-90" : "hover:shadow-lg hover:-translate-y-0.5"}
            ${
              currentTeamId === installation.team_id
                ? "bg-gradient-to-r from-brand/90 to-brand text-white shadow-md"
                : installation.is_active
                  ? "bg-white text-gray-700 hover:bg-gray-50/90"
                  : "bg-gray-100 text-gray-700"
            }
          `}
        >
          <div className="flex items-center justify-between">
            <span className="text-base font-medium flex items-center">
              {!installation.is_active && (
                <span
                  className="mr-2 text-amber-500"
                  title="Reconnection required"
                >
                  <FontAwesomeIcon icon={faWarning} />
                </span>
              )}
              {installation.team_name}
            </span>

            {!installation.is_active && (
              <div
                onClick={(e) => handleReconnect(installation.team_id, e)}
                className="text-sm px-2 py-1 rounded bg-brand text-white hover:bg-brand/90 transition-colors cursor-pointer"
                title="Reconnect to Slack"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleReconnect(installation.team_id, e);
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faArrowRightToBracket}
                  className="mr-1"
                />
                Reconnect
              </div>
            )}
          </div>

          {!installation.is_active && (
            <p className="text-xs text-gray-500 mt-1 text-left">
              Disconnected. Click to reconnect.
            </p>
          )}

          {/* Optional subtle border animation on hover */}
          <div
            className={`
            absolute inset-0 rounded-xl border-2 
            transition-colors duration-200
            ${
              currentTeamId === installation.team_id
                ? "border-transparent"
                : installation.is_active
                  ? "border-gray-100 group-hover:border-brand/20"
                  : "border-amber-200"
            }
          `}
          />
        </button>
      ))}
    </div>
  );
};

export default WorkspaceSwitcher;
